import type { SelectProps as AriaSelectProps, ValidationResult } from "react-aria-components";
import { Button, ListBox, ListBoxItem, Popover, Select, SelectValue } from "react-aria-components";
import type { ComponentPropsWithRef } from "react";
import React from "react";
import { tv } from "tailwind-variants";

import CheckmarkIcon from "@/assets/images/icons2/icon--checkmark.svg?component";
import ChevronIcon from "@/assets/images/icons2/icon--chevron.svg?component";
import { cn } from "@/lib/helpers/cn";

import { FieldError2 } from "../atoms/FieldError";
import { Label2 } from "../atoms/Label";
import { Text2 } from "../atoms/Text";

export interface BaseItem {
  id: number | string;
  name: string;
}

export type SelectProps<T extends BaseItem> = {
  className?: string;
  description?: string;
  errorMessage?: ((validation: ValidationResult) => string) | string;
  isDisabled?: boolean;
  isRequired?: boolean;
  label: string;
  options: T[];
  placeholder?: string;
} & Omit<AriaSelectProps<T>, "children"> &
  Pick<ComponentPropsWithRef<"select">, "autoComplete" | "form" | "name">;

export function Select2<T extends BaseItem = BaseItem>({
  className,
  description,
  errorMessage,
  isDisabled,
  isInvalid,
  isRequired,
  label,
  name,
  options,
  placeholder,
  ...props
}: SelectProps<T>) {
  const { button, chevronIcon, listBox, listBoxItem, popover, select, selectValue } =
    selectStyles();

  return (
    <Select
      className={cn(select(), className)}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isRequired={isRequired}
      name={name}
      {...props}
    >
      <Label2 isInvalid={isInvalid} isRequired={isRequired} label={label} />

      <Button className={cn(button(), "atom-focus group")}>
        <SelectValue className={selectValue()}>
          {({ selectedText }) => selectedText || placeholder || "Select..."}
        </SelectValue>

        <ChevronIcon className={chevronIcon()} />
      </Button>

      {description && !isInvalid && <Text2 slot="description" text={description} />}

      <FieldError2>{errorMessage}</FieldError2>

      <Popover className={popover()}>
        <ListBox className={listBox()} items={options}>
          {(item) => (
            <ListBoxItem
              className={cn(listBoxItem(), "atom-keyboard-focus")}
              id={item.id}
              textValue={item.name}
            >
              {({ isSelected }) => (
                <>
                  {item.name}
                  {isSelected && <CheckmarkIcon />}
                </>
              )}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </Select>
  );
}

export const selectStyles = tv({
  defaultVariants: {
    size: "md",
  },
  slots: {
    button: cn(
      "tw:h-11 tw:gap-2 tw:px-2.5 tw:py-2.5 tw:text-sm",
      "tw:flex tw:w-full tw:items-center tw:justify-between",
      "tw:rounded-sm! tw:border tw:border-solid tw:border-stroke-tertiary tw:bg-fill-white",
      "tw:cursor-pointer tw:rounded",
      "tw:transition-all tw:duration-150",
      "tw:hover:border-stroke-secondary",
      "tw:data-disabled:border-stroke-tertiary tw:data-disabled:bg-fill-senary",
      "tw:disabled:cursor-not-allowed tw:disabled:text-text-quinary",
      "tw:group-data-invalid:border-stroke-error-primary",
    ),
    chevronIcon: cn(
      "tw:h-6 tw:w-6 tw:text-fill-secondary",
      "tw:transition-transform tw:duration-200",
      "tw:group-data-open:rotate-180",
    ),
    listBox: cn("tw:max-h-75 tw:overflow-y-auto", "tw:outline-none", "tw:py-1"),
    listBoxItem: cn(
      "tw:flex tw:items-center tw:justify-between",
      "tw:text-sm",
      "tw:px-2.5 tw:py-2",
      "tw:cursor-pointer tw:text-text-primary!",
      "tw:outline-none",
      "tw:transition-colors tw:duration-150",
      "tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap",
      "tw:hover:bg-fill-bright-purple-quinary",
      "tw:data-focused:bg-fill-bright-purple-quinary",
      "tw:data-focus-visible:bg-fill-bright-purple-quinary",
      "tw:selected:bg-fill-bright-purple-quaternary",
      "tw:disabled:cursor-not-allowed tw:disabled:text-text-quaternary",
    ),
    popover: cn(
      "tw:w-(--trigger-width)",
      "tw:bg-fill-white",
      "tw:border tw:border-solid tw:border-stroke-tertiary",
      "tw:rounded tw:shadow-lg",
    ),
    select: "tw:group tw:flex tw:flex-col tw:gap-1.5 tw:w-full",
    selectValue: cn(
      "tw:min-w-0 tw:flex-1 tw:text-left tw:text-sm",
      "tw:data-placeholder:text-text-quinary",
    ),
  },
});
